import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { DryRunOutDTO, RuleAst } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import { TypedRuleAst } from '../ruleAst';
import { actionLabel } from '../i18nKeys';
import { useRuleMetadata } from '../metadata/RuleMetadataProvider';
import { useRuleApi } from '../useRuleApi';
import ExplainTree from '../ExplainTree';
import ActionParamsSummary from '../ActionParamsSummary';
import DiagnosticsPanel from './DiagnosticsPanel';

interface ComponentProps {
  sourceText: string;
  ast?: TypedRuleAst;
  /** Builder mode sends the AST, text mode the source — exactly one, never both. */
  useAst: boolean;
  companyId?: string;
  disabled?: boolean;
}

/**
 * Evaluates the rule against a real expedition and shows what would have happened, without acting.
 *
 * A rule that silently never matches is the most likely complaint this feature will attract, and
 * this is the answer to it — so it sits beside the editor rather than behind a menu, and it works
 * before the rule has ever been saved.
 */
const DryRunPanel: React.FC<ComponentProps> = ({ sourceText, ast, useAst, companyId, disabled }) => {
  const { tr } = useTranslationLgs();
  const { dryRun } = useRuleApi(companyId);
  const { actionByKey } = useRuleMetadata();

  const [entityRef, setEntityRef] = useState('');
  const [result, setResult] = useState<DryRunOutDTO>(undefined);
  const [running, setRunning] = useState(false);

  const run = () => {
    if (!entityRef.trim()) return;
    setRunning(true);
    dryRun(
      // Exactly one of sourceText / ast is accepted, so the builder sends the tree it owns and
      // the text pane sends the text it owns.
      useAst ? { ast: ast as unknown as RuleAst, entityRef: entityRef.trim() } : { sourceText, entityRef: entityRef.trim() },
      data => {
        setResult(data);
        setRunning(false);
      },
      {
        onError: (_error, showStandardError) => {
          showStandardError();
          setRunning(false);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-[16rem] flex-1 flex-col gap-1">
          <span className="text-sm text-content-secondary">{tr('AiDispatcher.entityRef', 'Expedition number or id')}</span>
          <InputText value={entityRef} onChange={event => setEntityRef(event.target.value)} onKeyDown={event => event.key === 'Enter' && run()} disabled={disabled} />
        </label>
        <RingilButton
          mode="filled-rectangle"
          label={tr('AiDispatcher.runDryRun', 'Test this rule')}
          onClick={run}
          loading={running}
          disabled={disabled || !entityRef.trim() || (useAst && !ast)}
        />
      </div>

      {result && (
        <div className="flex flex-col gap-4">
          <div className={`rounded-md border border-solid p-3 ${result.matched ? 'border-green-300 bg-green-50' : 'border-base-tertiary bg-base-secondary'}`}>
            <span className="font-medium">
              {result.matched
                ? tr('AiDispatcher.matched', 'The rule matches this expedition.')
                : tr('AiDispatcher.notMatched', 'The rule does not match this expedition.')}
            </span>
          </div>

          {result.diagnostics?.length > 0 && <DiagnosticsPanel diagnostics={result.diagnostics} />}

          <section className="flex flex-col gap-2">
            <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.explain', 'Why')}</h4>
            <ExplainTree explain={result.explain} companyId={companyId} />
          </section>

          <section className="flex flex-col gap-2">
            <h4 className="m-0 text-sm font-medium uppercase text-content-secondary">{tr('AiDispatcher.plannedActions', 'What would happen')}</h4>
            {(result.plannedActions ?? []).length === 0 && <span className="text-sm text-content-secondary">{tr('AiDispatcher.noPlannedActions', 'No actions.')}</span>}
            {(result.plannedActions ?? []).map((action, index) => (
              <div key={index} className={`rounded-md border border-solid p-3 ${action.wouldRefuse ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
                <div className="flex flex-wrap items-baseline gap-2">
                  {/* The action's own name, as the catalogue words it — the key is kept beside it
                      because it is what the rule text says. */}
                  <span className="text-sm font-medium">{actionLabel(tr, action.action, actionByKey(action.action)?.i18nKey)}</span>
                  <span className="font-mono text-xs text-content-tertiary">{action.action}</span>
                </div>
                <div className="mt-1">
                  <ActionParamsSummary params={action.params as Record<string, unknown>} action={action.action} companyId={companyId} />
                </div>
                {/* An action refused for its own reasons is often the real answer, and it is
                    invisible from the explain trace alone. */}
                {action.wouldRefuse && <div className="mt-1 text-sm text-amber-700">{action.wouldRefuse}</div>}
              </div>
            ))}
          </section>
        </div>
      )}
    </div>
  );
};

export default DryRunPanel;
